import { createSlice } from '@reduxjs/toolkit'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey';
import { AppIsInTestEnvironment } from 'functions/environmentUtils'
import moment from 'moment'

const initialState = {
    profileType: null,
    groupId: null,
    athleteId: null,
    filters: {
        workoutTemplateFilter: 'strength-session-a,strength-session-b,mobility-a,mobility-b',
        phaseFilter: 'Warm Up,Prep,Main 1,Main 2,Accessory 1,Mobility Circuit',
        slotFilter: [0, 1, 2, 3]
    },
    kinesisEvents: [],
    pastData: [],
    activeData: null,
    latestProgramSnapshot: null,
    trainingChartPresentDate: moment.utc().local().format('YYYY-MM-DD'),
    trainingChartPresentWeekOfTheYearKey: new WeekOfTheYearKey(moment.utc().local()).toString(),
    trainingChartSelectedWeekOfTheYearKey: new WeekOfTheYearKey(moment.utc().local()).toString(),
    trainingChartUILocation: '',
    trainingChartSegmentDomain: 8,
    trainingChartSegment: [],
    trainingChartSelectedWeekData: null,
    trainingChartSelectedActiveARaceProgramTableColumn: null,
    programTableOverrides: [],
    draggedSlots: {
        isDragging: false,
        slots: []
    },
    workoutTemplateOverrideWeightRecommendationStorage: null,
    injuries: [],
    injuryHistory: []
}

const trainingChartSlice = createSlice({
    name: 'trainingChart',
    initialState,
    reducers: {
        initializeTrainingChartData: (state, action) => {

            const { 
                profileType,
                groupId,
                athleteId, 
                trainingChartUILocation, 
                kinesisEvents, 
                pastData, 
                activeData, 
                latestProgramSnapshot 
            } = action.payload

            state.profileType = profileType
            state.groupId = groupId
            state.athleteId = athleteId
            state.trainingChartUILocation = trainingChartUILocation
            state.kinesisEvents = kinesisEvents
            state.pastData = pastData
            state.activeData = activeData
            state.latestProgramSnapshot = latestProgramSnapshot

            state.trainingChartSelectedWeekData = GetTrainingChartWeekData(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))
            state.trainingChartSelectedActiveARaceProgramTableColumn = GetActiveARaceProgramTableColumn(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))
            CreateTrainingChartSegment(state)

        },
        setTrainingChartSelectedWeekOfTheYearKey: (state, action) => {

            state.trainingChartSelectedWeekOfTheYearKey = action.payload

            state.trainingChartSelectedWeekData = GetTrainingChartWeekData(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))
            state.trainingChartSelectedActiveARaceProgramTableColumn = GetActiveARaceProgramTableColumn(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))

            CreateTrainingChartSegment(state)

        },
        setTrainingChartSegmentDomain: (state, action) => { 
            state.trainingChartSegmentDomain = action.payload 
        },
        updateTrainingChartSegmentWithCompletionData: (state, action) => {

            state.trainingChartSegment[action.payload.index].workoutTemplateCompletionData = action.payload.workoutTemplateCompletionData

        },
        mutateActiveARaceProgramTable: (state, action) => {

            const targetProgramTableColumn = state.latestProgramSnapshot.programTable.find(week => week.programWeekIndex === action.payload.programWeekIndex);

            if (targetProgramTableColumn === undefined || targetProgramTableColumn === null) {
                console.error('Target program table column not found when trying to modify redux store program table!!')
                return
            }

            const targetSetsAndRepsData = targetProgramTableColumn.volumeData.setsAndRepsData[action.payload.workoutTemplateKey]
            const targetExerciseData = targetProgramTableColumn.exerciseData[action.payload.workoutTemplateKey]

            const phaseIndex = action.payload.phaseIndex

            switch (action.payload.key) {

                case 'exercise':

                    targetExerciseData[phaseIndex].exercises[action.payload.exerciseSlotIndex] = action.payload.overrideExercise

                    break

                case 'set':

                    if (action.payload.buttonType === 'minus' && targetSetsAndRepsData[phaseIndex].sets > 1) {
                        targetSetsAndRepsData[phaseIndex].sets -= 1
                    }

                    if (action.payload.buttonType === 'plus') {
                        targetSetsAndRepsData[phaseIndex].sets += 1
                    }

                    break

                case 'reps-unilateral':

                    if (action.payload.buttonType === 'minus' && targetSetsAndRepsData[phaseIndex].reps[action.payload.exerciseSlotIndex].unilateral > 1) {
                        targetSetsAndRepsData[phaseIndex].reps[action.payload.exerciseSlotIndex].unilateral -= 1
                    }

                    if (action.payload.buttonType === 'plus') {
                        targetSetsAndRepsData[phaseIndex].reps[action.payload.exerciseSlotIndex].unilateral += 1
                    }

                    break

                case 'reps-bilateral':

                    if (action.payload.buttonType === 'minus' && targetSetsAndRepsData[phaseIndex].reps[action.payload.exerciseSlotIndex].bilateral > 1) {
                        targetSetsAndRepsData[phaseIndex].reps[action.payload.exerciseSlotIndex].bilateral -= 1
                    }

                    if (action.payload.buttonType === 'plus') {
                        targetSetsAndRepsData[phaseIndex].reps[action.payload.exerciseSlotIndex].bilateral += 1
                    }

                    break

                case 'add-weight-override':

                    targetSetsAndRepsData[phaseIndex].reps[action.payload.exerciseSlotIndex].weightOverride = 5

                    break

                case 'remove-weight-override':

                    delete targetSetsAndRepsData[phaseIndex].reps[action.payload.exerciseSlotIndex].weightOverride

                    break

                case 'set-weight-override':

                    targetSetsAndRepsData[phaseIndex].reps[action.payload.exerciseSlotIndex].weightOverride = parseInt(action.payload.weight)

                    break

                default:
                    console.log('Unknown key: ', action.payload.key)
                    break

            }

            state.trainingChartSelectedWeekData = GetTrainingChartWeekData(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))
            state.trainingChartSelectedActiveARaceProgramTableColumn = GetActiveARaceProgramTableColumn(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))

        },
        setTrainingChartWorkoutTemplateFilter: (state, action) => { state.filters.workoutTemplateFilter = action.payload },
        setTrainingChartPhaseFilter: (state, action) => { state.filters.phaseFilter = action.payload },
        setTrainingChartSlotFilter: (state, action) => { state.filters.slotFilter = action.payload },
        setSlotDraggingStatus: (state, action) => { state.draggedSlots.isDragging = action.payload; },
        addDraggedSlot: (state, action) => {

            // If there are no dragged slots, add the first one
            if (state.draggedSlots.slots.length === 0) state.draggedSlots.slots.push(action.payload);

            // If there are dragged slots, check if the new slot is already in the array
            if (state.draggedSlots.slots.length > 0) {

                const exists = state.draggedSlots.slots.some(slot =>
                    slot.weekOfTheYearKey === action.payload.weekOfTheYearKey &&
                    slot.workoutTemplateKey === action.payload.workoutTemplateKey &&
                    slot.phaseIndex === action.payload.phaseIndex &&
                    slot.slotIndex === action.payload.slotIndex
                );

                // If the new slot is not in the array, add it
                if (!exists) state.draggedSlots.slots.push(action.payload);

            }

        },
        removeDraggedSlot: (state, action) => {

            state.draggedSlots.slots = state.draggedSlots.slots.filter(slot =>
                !(slot.phaseIndex === action.payload.phaseIndex &&
                    slot.slotIndex === action.payload.slotIndex &&
                    slot.workoutTemplateKey === action.payload.workoutTemplateKey &&
                    slot.weekOfTheYearKey === action.payload.weekOfTheYearKey)
            );

        },
        clearDraggedSlots: (state) => {

            state.draggedSlots.isDragging = false;
            state.draggedSlots.slots = [];

        },
        addNewProgramTableOverride: (state, action) => {
            state.programTableOverrides.push(action.payload)
        },
        bulkAddProgramTableOverrides: (state, action) => {
            state.programTableOverrides = [...state.programTableOverrides, ...action.payload]
        },
        removeProgramTableOverride: (state, action) => {
            const filtered = state.programTableOverrides.filter((_, index) => index !== action.payload);
            state.programTableOverrides = filtered.length === 0 ? [] : filtered;
        },
        bulkRemoveProgramTableOverrides: (state, action) => {
            state.programTableOverrides = state.programTableOverrides.filter((_, index) => !action.payload.includes(index));
        },
        clearProgramTableOverrides: (state) => {
            state.programTableOverrides = []
        },
        setInjuries: (state, action) => { state.injuries = action.payload },
        setInjuryHistory: (state, action) => { state.injuryHistory = action.payload },
        resetTrainingChartState: () => initialState,
        TEST_setTrainingChartPresentDate: (state, action) => {
            if (AppIsInTestEnvironment()) {
                state.trainingChartPresentDate = action.payload
            }
        },
        TEST_setTrainingChartPresentWeekOfTheYearKey: (state, action) => {
            if (AppIsInTestEnvironment()) {

                state.trainingChartPresentWeekOfTheYearKey = action.payload

                state.trainingChartSelectedWeekOfTheYearKey = action.payload

                state.trainingChartSelectedWeekData = GetTrainingChartWeekData(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))
                state.trainingChartSelectedActiveARaceProgramTableColumn = GetActiveARaceProgramTableColumn(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))

            }
        }
    },
});

const CreateTrainingChartSegment = (state) => {

    var newTrainingChartSegment = []

    // Get week of the year keys for the entire segment
    var segmentWeekOfTheYearKeys = []
    for (let i = 0; i < state.trainingChartSegmentDomain; i++) segmentWeekOfTheYearKeys.push(new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey).add(i))

    // Loop over every week of the year key and find the matching week in the training chart data
    segmentWeekOfTheYearKeys.forEach((weekOfTheYearKey) => {
        var item = CreateTrainingChartSegmentObject(state, weekOfTheYearKey)
        newTrainingChartSegment.push(item)
    })

    // console.log(newTrainingChartSegment)

    state.trainingChartSegment = newTrainingChartSegment

}

const CreateTrainingChartSegmentObject = (state, weekOfTheYearKey) => {

    var segmentObject = {
        'weekOfTheYearKey': weekOfTheYearKey.toString(),
        'trainingChartDataKey': 'empty',
        'programTableColumn': null,
        'overviewFile': null,
        'workoutTemplateCompletionData': {
            'loading': false,
            'strength-session-a': null,
            'strength-session-b': null,
            'mobility-a': null,
            'mobility-b': null
        }
    }

    // Check past data

    for (let item of state.pastData) {

        if (new WeekOfTheYearKey(item.weekOfTheYearKey).equals(weekOfTheYearKey)) {

            segmentObject.trainingChartDataKey = 'past'
            segmentObject.overviewFile = item
            segmentObject.workoutTemplateCompletionData.loading = true

            return segmentObject

        }

    }

    // Check active data

    if (state.activeData !== null && new WeekOfTheYearKey(state.activeData.weekOfTheYearKey).equals(weekOfTheYearKey)) {

        segmentObject.trainingChartDataKey = 'active'
        segmentObject.workoutTemplateCompletionData.loading = true
        segmentObject.overviewFile = state.activeData

    }

    // Check future data

    if (state.latestProgramSnapshot !== null && state.latestProgramSnapshot.programTable !== null) {

        for (let item of state.latestProgramSnapshot.programTable) {

            if (new WeekOfTheYearKey(item.weekOfTheYearKey).equals(weekOfTheYearKey)) {

                // For athlete profile, only show future weeks if they are in the present or future
                if (state.trainingChartUILocation === 'athlete-profile') {

                    if (
                        new WeekOfTheYearKey(state.trainingChartPresentWeekOfTheYearKey).equals(new WeekOfTheYearKey(item.weekOfTheYearKey))
                        || new WeekOfTheYearKey(state.trainingChartPresentWeekOfTheYearKey).lessThan(new WeekOfTheYearKey(item.weekOfTheYearKey))
                    ) {

                        // If the object is already active, splice together active and future data

                        if (segmentObject.trainingChartDataKey === 'active') {

                            segmentObject.programTableColumn = item

                            return segmentObject

                        }

                        // Otherwise just add the future data

                        if (segmentObject.trainingChartDataKey === 'empty') {

                            segmentObject.trainingChartDataKey = 'future'
                            segmentObject.programTableColumn = item

                            return segmentObject

                        }

                    }


                }

                // For snapshot generator, display future weeks even if they are in the past
                if (state.trainingChartUILocation === 'snapshot-generator') {

                    // If the object is already active, splice together active and future data

                    if (segmentObject.trainingChartDataKey === 'active') {

                        segmentObject.programTableColumn = item

                        return segmentObject

                    }

                    // Otherwise just add the future data

                    if (segmentObject.trainingChartDataKey === 'empty') {

                        segmentObject.trainingChartDataKey = 'future'
                        segmentObject.programTableColumn = item

                        return segmentObject

                    }

                }

            }

        }

    }

    return segmentObject

}

const GetTrainingChartWeekData = (state, weekOfTheYearKey) => {

    // First check past data
    for (let item of state.pastData) {

        if (new WeekOfTheYearKey(item.weekOfTheYearKey).equals(weekOfTheYearKey)) return item

    }

    // Then check active data
    if (state.activeData !== null && new WeekOfTheYearKey(state.activeData.weekOfTheYearKey).equals(weekOfTheYearKey)) {
        return state.activeData
    }

    // Then check future data
    if (state.latestProgramSnapshot !== null && state.latestProgramSnapshot.programTable !== null) {

        for (let item of state.latestProgramSnapshot.programTable) {

            if (new WeekOfTheYearKey(item.weekOfTheYearKey).equals(weekOfTheYearKey))
                return item

        }

    }

    return null

}

const GetActiveARaceProgramTableColumn = (state, weekOfTheYearKey) => {

    if (state.latestProgramSnapshot !== null && state.latestProgramSnapshot.programTable !== null) {

        for (let item of state.latestProgramSnapshot.programTable) {
            if (new WeekOfTheYearKey(item.weekOfTheYearKey).equals(weekOfTheYearKey))
                return item
        }

    }

    return null

}

export const {
    initializeTrainingChartData,
    setTrainingChartSelectedWeekOfTheYearKey,
    setTrainingChartSegmentDomain,
    updateTrainingChartSegmentWithCompletionData,
    mutateActiveARaceProgramTable,
    setTrainingChartWorkoutTemplateFilter,
    setTrainingChartPhaseFilter,
    setTrainingChartSlotFilter,
    setSlotDraggingStatus,
    addDraggedSlot,
    removeDraggedSlot,
    clearDraggedSlots,
    addNewProgramTableOverride,
    bulkAddProgramTableOverrides,
    removeProgramTableOverride,
    bulkRemoveProgramTableOverrides,
    clearProgramTableOverrides,
    setInjuries,
    setInjuryHistory,
    resetTrainingChartState,
    TEST_setTrainingChartPresentDate,
    TEST_setTrainingChartPresentWeekOfTheYearKey
} = trainingChartSlice.actions;

export default trainingChartSlice.reducer;